<template>
  <div
    class="ip-address-input form-control d-flex align-items-center"
    :class="{ 'is-invalid': invalid, 'is-disabled': disabled }"
  >
    <input
      ref="input0"
      :value="octets[0]"
      type="text"
      class="octet-input"
      maxlength="3"
      placeholder="0"
      :disabled="disabled"
      @input="handleInput(0, $event)"
      @keydown="handleKeydown(0, $event)"
      @paste="handlePaste"
      @focus="$event.target.select()"
    />
    <span class="separator">.</span>
    <input
      ref="input1"
      :value="octets[1]"
      type="text"
      class="octet-input"
      maxlength="3"
      placeholder="0"
      :disabled="disabled"
      @input="handleInput(1, $event)"
      @keydown="handleKeydown(1, $event)"
      @paste="handlePaste"
      @focus="$event.target.select()"
    />
    <span class="separator">.</span>
    <input
      ref="input2"
      :value="octets[2]"
      type="text"
      class="octet-input"
      maxlength="3"
      placeholder="0"
      :disabled="disabled"
      @input="handleInput(2, $event)"
      @keydown="handleKeydown(2, $event)"
      @paste="handlePaste"
      @focus="$event.target.select()"
    />
    <span class="separator">.</span>
    <input
      ref="input3"
      :value="octets[3]"
      type="text"
      class="octet-input"
      maxlength="3"
      placeholder="0"
      :disabled="disabled"
      @input="handleInput(3, $event)"
      @keydown="handleKeydown(3, $event)"
      @paste="handlePaste"
      @focus="$event.target.select()"
    />
  </div>
</template>

<script>
import { ref, watch } from "@vue/composition-api";

export default {
  name: "IpAddressInput",
  props: {
    value: {
      type: String,
      default: "",
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit, refs }) {
    const octets = ref(["", "", "", ""]);
    const input0 = ref(null);
    const input1 = ref(null);
    const input2 = ref(null);
    const input3 = ref(null);

    // Watch prop value
    watch(
      () => props.value,
      (val) => {
        const current = octets.value.join(".");
        if (val !== current) {
          if (val) {
            const parts = val.split(".");
            if (parts.length === 4) {
              octets.value = parts;
            }
          } else {
            octets.value = ["", "", "", ""];
          }
        }
      },
      { immediate: true }
    );

    const updateValue = () => {
      const ip = octets.value.map((o) => (o === "" ? "" : o)).join(".");
      emit("input", ip);
    };

    const focusInput = (index) => {
      const inputRef = [input0, input1, input2, input3][index];
      if (inputRef.value) {
        inputRef.value.focus();
      }
    };

    const handleInput = (index, event) => {
      let val = event.target.value;

      // Remove non-digits
      val = val.replace(/\D/g, "");

      if (val !== "") {
        let num = parseInt(val, 10);
        if (num > 255) {
          val = "255";
        } else {
          val = String(num);
        }
      }

      event.target.value = val;
      octets.value[index] = val;

      // Auto-advance if 3 digits or value is >= 26 (can't be valid with another digit)
      if (
        (val.length === 3 || (val.length === 2 && parseInt(val, 10) >= 26)) &&
        index < 3
      ) {
        focusInput(index + 1);
      }

      updateValue();
    };

    const handleKeydown = (index, event) => {
      // Handle dot to advance
      if (event.key === "." || event.key === "Decimal") {
        event.preventDefault();
        if (index < 3) {
          focusInput(index + 1);
        }
      }
      // Handle backspace to go back
      if (event.key === "Backspace" && octets.value[index] === "" && index > 0) {
        event.preventDefault();
        focusInput(index - 1);
      }
      // Handle arrows
      if (
        event.key === "ArrowRight" &&
        event.target.selectionStart === event.target.value.length &&
        index < 3
      ) {
        event.preventDefault();
        focusInput(index + 1);
      }
      if (event.key === "ArrowLeft" && event.target.selectionStart === 0 && index > 0) {
        event.preventDefault();
        focusInput(index - 1);
      }
    };

    const handlePaste = (event) => {
      event.preventDefault();
      const paste = (event.clipboardData || window.clipboardData).getData("text");
      if (paste && (paste.includes(".") || paste.length > 3)) {
        const parts = paste.split(".");

        if (parts.length > 1) {
          parts.forEach((part, i) => {
            if (i < 4) {
              let val = part.replace(/\D/g, "").substring(0, 3);
              if (val && parseInt(val) > 255) val = "255";
              octets.value[i] = val;
            }
          });
          updateValue();
          // Focus last filled
          const lastIndex = Math.min(parts.length - 1, 3);
          focusInput(lastIndex);
        }
      }
    };

    return {
      octets,
      input0,
      input1,
      input2,
      input3,
      handleInput,
      handleKeydown,
      handlePaste,
    };
  },
};
</script>

<style scoped>
.ip-address-input {
  padding: 0.375rem 0.75rem;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  width: 100%;
  display: flex;
  align-items: center;
}

.ip-address-input:focus-within {
  border-color: #4a708d;
  box-shadow: 0 0 0 0.2rem rgba(74, 112, 141, 0.25);
}

.octet-input {
  border: none;
  outline: none;
  text-align: center;
  width: 100%;
  min-width: 0;
  padding: 0;
  margin: 0;
  background: transparent;
  color: inherit;
}

.separator {
  margin: 0 2px;
  font-weight: bold;
  color: #6e6b7b;
}

.is-invalid {
  border-color: #ea5455;
}

.is-disabled {
  background-color: #efefef;
  cursor: not-allowed;
  opacity: 0.65;
}

.dark-layout .ip-address-input {
  background-color: var(--second-dark);
  color: #b4b7bd;
}
</style>
